import { OpenShift } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { wouldProposedShiftCauseOverlap } from "../doShiftsExcessivelyOverlap";
import type { BundleShiftPay, ShiftTimeData } from "../types";
import { wouldProposedShiftCauseOverworking } from "../wouldProposedShiftCauseOverworking";

interface UseShiftBundleSelectionProps {
  bundleShifts: OpenShift[];
  bookedShifts: ShiftTimeData[];
  requiredShiftCount: number;
  totalShiftCount: number;
  bundleShiftPays: Map<string, BundleShiftPay>;
}

interface DisabledShiftInfo {
  reason: string;
}

export function useShiftBundleSelection(props: UseShiftBundleSelectionProps) {
  const { bundleShifts, bookedShifts, requiredShiftCount, totalShiftCount, bundleShiftPays } =
    props;

  const isConfirmationMode = requiredShiftCount === totalShiftCount;

  const [selectedShiftIds, setSelectedShiftIds] = useState<Set<string>>(() => {
    if (isConfirmationMode) {
      return new Set(bundleShifts.map((shift) => shift.id));
    }

    return new Set();
  });

  const hasSeededConfirmation = useRef(false);

  useEffect(() => {
    if (isConfirmationMode && bundleShifts.length > 0 && !hasSeededConfirmation.current) {
      hasSeededConfirmation.current = true;
      setSelectedShiftIds(new Set(bundleShifts.map((shift) => shift.id)));
    }
  }, [isConfirmationMode, bundleShifts]);

  const selectedShifts = useMemo(
    () => bundleShifts.filter((shift) => selectedShiftIds.has(shift.id)),
    [bundleShifts, selectedShiftIds]
  );

  const disabledShiftMap = useMemo(() => {
    const selectedShiftTimeData: ShiftTimeData[] = selectedShifts.map((shift) => ({
      start: shift.attributes.start,
      end: shift.attributes.end,
    }));

    const existingShifts: ShiftTimeData[] = [...bookedShifts, ...selectedShiftTimeData];

    const disabled = new Map<string, DisabledShiftInfo>();

    for (const shift of bundleShifts) {
      if (selectedShiftIds.has(shift.id)) {
        continue;
      }

      const shiftTimeData = { start: shift.attributes.start, end: shift.attributes.end };

      // Check overlap against only selected shifts, since shift overlapping
      // with booked shifts would not be returned as open shifts.
      if (wouldProposedShiftCauseOverlap(shiftTimeData, selectedShiftTimeData)) {
        disabled.set(shift.id, { reason: "Shift overlap" });
        continue;
      }

      if (wouldProposedShiftCauseOverworking(shiftTimeData, existingShifts)) {
        disabled.set(shift.id, { reason: "Over hour limit" });
      }
    }

    return disabled;
  }, [bundleShifts, bookedShifts, selectedShifts, selectedShiftIds]);

  const toggleShift = useCallback((shiftId: string) => {
    setSelectedShiftIds((previousSelectedShiftIds) => {
      const nextSelectedShiftIds = new Set(previousSelectedShiftIds);
      if (nextSelectedShiftIds.has(shiftId)) {
        nextSelectedShiftIds.delete(shiftId);
      } else {
        nextSelectedShiftIds.add(shiftId);
      }

      return nextSelectedShiftIds;
    });
  }, []);

  const selectedCount = selectedShiftIds.size;

  const totalSelectedPayInMinorUnits = useMemo(() => {
    let total = 0;
    for (const shift of selectedShifts) {
      const totalPayInMinorUnits = bundleShiftPays.get(shift.id)?.totalPayInMinorUnits;
      if (isDefined(totalPayInMinorUnits)) {
        total += totalPayInMinorUnits;
      }
    }

    return total;
  }, [selectedShifts, bundleShiftPays]);

  const allUnselectedAreDisabled = useMemo(() => {
    const unselectedShifts = bundleShifts.filter((shift) => !selectedShiftIds.has(shift.id));
    return (
      unselectedShifts.length > 0 &&
      unselectedShifts.every((shift) => disabledShiftMap.has(shift.id))
    );
  }, [bundleShifts, selectedShiftIds, disabledShiftMap]);

  const isBookingAllowed =
    selectedCount >= requiredShiftCount || (selectedCount > 0 && allUnselectedAreDisabled);

  return {
    selectedShiftIds,
    selectedCount,
    disabledShiftMap,
    toggleShift,
    totalSelectedPayInMinorUnits,
    isBookingAllowed,
    isConfirmationMode,
  };
}
